import { getDifferenceInMinutes } from "../date";
import { guardNumber } from "../numbers";
import type { AssignmentReferenceSingleBookingSchema } from "../schemas";
import type { Nullable, NullableFields } from "../types";

/**
 * This function calculates the total minutes of all bookings.
 * It takes an array of bookings and returns the total minutes.
 * If the bookings array is empty or not provided, it returns 0.
 * It iterates through each booking, calculates the minutes for each booking using the calculateBooking function,
 * and adds them to the total minutes.
 * If the booking is not valid, it skips it.
 * The function returns the total minutes of all bookings.
 * @param bookings
 * @returns
 */
export function calculateBookingMinutes(
	bookings: Nullable<
		Nullable<
			NullableFields<Pick<AssignmentReferenceSingleBookingSchema, "checkIn" | "checkOut" | "pause">>
		>[]
	>,
) {
	if (!bookings) return 0;
	if (!bookings.length) return 0;
	let minutes = 0;
	for (const booking of bookings) {
		const bookingMinutes = calculateBooking(booking);
		if (!bookingMinutes) continue;
		minutes += bookingMinutes;
	}
	return minutes;
}

/**
 * This function calculates the total minutes of a booking.
 * It takes into account the check-in and check-out times, as well as any pauses.
 * If the check-in or check-out time is not provided, it returns 0.
 * If the total minutes is less than or equal to 0, it returns 0.
 * Otherwise, it returns the total minutes minus the pause time in seconds.
 * @param booking
 * @returns
 */
export function calculateBooking(
	booking: Nullable<
		NullableFields<
			Pick<AssignmentReferenceSingleBookingSchema, "checkIn" | "checkOut" | "pause">
		> & {
			result?: string | null;
		}
	>,
) {
	if (!booking) return 0;
	if (booking.result === "rejected") return 0;
	let minutes = 0;
	if (!booking.checkIn || !booking.checkOut) return minutes;
	const checkIn = booking.checkIn;
	const checkOut = booking.checkOut;
	const pause = guardNumber(booking.pause);
	const diffMinutes = getDifferenceInMinutes([checkIn, checkOut]);
	const totalMinutes = diffMinutes - pause * 60;
	if (totalMinutes > 0) {
		minutes = totalMinutes;
	}
	return minutes;
}
